import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, Loader2, CircleDashed } from 'lucide-react';
import type { ConnectStatus } from '@/lib/api';
import {
  countryMismatch, payoutCountryFrom, payoutCountryLabelKey, payoutCountryTextKey,
} from '@/lib/payoutCountry';
import styles from './ConnectStatusCard.module.css';

/**
 * One honest read of the resident's Stripe Express account, shared by
 * the earn walk's "Get paid" step and the /my-account/connect/complete
 * landing so the two can never disagree about what Stripe wants.
 *
 * States, in priority order — the first that matches wins:
 *
 *   ready       charges_enabled && payouts_enabled. Done.
 *   blocked     Stripe has stopped the account for a reason no form
 *               fixes — rejected.*, under_review, platform_paused,
 *               listed, other (requirements_disabled_reason). Named,
 *               and the caller stops polling: nothing the resident
 *               does in Stripe's form changes it.
 *   confirming  Stripe HAS the fields and is verifying them
 *               (pending_verification non-empty, nothing currently
 *               due). This is the gap between the resident landing
 *               back from Stripe and account.updated flipping the
 *               flags — usually minutes. The CALLER polls while the
 *               card reports this state (see `connectCardState`).
 *   error       Stripe named a verification failure. The code is
 *               mapped to plain copy where we know it; otherwise
 *               Stripe's own reason text is shown verbatim, because a
 *               rejection the resident cannot read teaches nothing.
 *   needed      requirements_currently_due non-empty: list what
 *               Stripe still wants, humanised. Only THIS state says
 *               something is needed — a pending verification is not a
 *               request, and telling the resident to "continue setup"
 *               while Stripe is still thinking sends them into a form
 *               with nothing to fill.
 *   notStarted  has_account false.
 *
 * Under every state but ready, one line says guests can't pay at the
 * charger until Stripe setup is complete (item 7, 2026-10-02): the
 * sharing gates refuse Open to guests and Turn on earning until then.
 * With an account, its country shows read-only ("Payout account
 * country"), because Stripe cannot change it; when the host's home is in
 * the other country a warning says to contact support. Never a way to
 * change it here.
 *
 * Presentation only. The card never calls Stripe or the API; the
 * caller owns the query (and the polling) so it can decide what to
 * do with the answer.
 */

export type ConnectCardState = 'ready' | 'blocked' | 'confirming' | 'error' | 'needed' | 'notStarted';

/**
 * disabled_reason values that are the resident's to act on through
 * Stripe's own form (the other states already say what): everything
 * else is a platform/risk hold. Stripe's documented set: requirements.*,
 * action_required.requested_capabilities, rejected.{fraud,
 * incomplete_verification, listed, other, terms_of_service}, listed,
 * platform_paused, under_review, other.
 */
function blockedReason(reason: string | null): 'underReview' | 'rejected' | 'paused' | null {
  if (!reason) return null;
  if (reason.startsWith('requirements.') || reason.startsWith('action_required.')) return null;
  if (reason === 'under_review') return 'underReview';
  if (reason.startsWith('rejected.')) return 'rejected';
  return 'paused';
}

/** The two arrays are new to GET /me/connect/status; a bundle served
 *  ahead of the Api task that returns them must not throw. */
const arr = (v: string[] | undefined | null): string[] => (Array.isArray(v) ? v : []);
const errs = (v: ConnectStatus['requirements_errors'] | undefined | null): ConnectStatus['requirements_errors'] => (Array.isArray(v) ? v : []);

/** Derive the card's state from a status. Exported so callers can
 *  gate polling (confirming) and the walk's primary button (ready)
 *  on the same rule the card renders — one source of truth. */
export function connectCardState(status: ConnectStatus): ConnectCardState {
  if (status.charges_enabled && status.payouts_enabled) return 'ready';
  if (blockedReason(status.requirements_disabled_reason)) return 'blocked';
  if (
    arr(status.requirements_pending_verification).length > 0
    && arr(status.requirements_currently_due).length === 0
  ) return 'confirming';
  // Errors before "needed": when Stripe rejects a document it re-asks
  // for the same field, so currently_due is non-empty too — and the
  // resident needs the reason, not just the field name.
  if (errs(status.requirements_errors).length > 0) return 'error';
  if (arr(status.requirements_currently_due).length > 0) return 'needed';
  if (!status.has_account) return 'notStarted';
  // Account exists, nothing due, nothing pending, flags still off.
  // Stripe has not finished its first pass yet — treat it as
  // confirming so the caller keeps polling rather than rendering an
  // empty "needed" list.
  return 'confirming';
}

/**
 * Stripe verification error codes we can explain better than Stripe
 * does. Anything not listed falls back to Stripe's `reason`, which is
 * already a sentence. Keys live in connect.json `card.errors.*`.
 */
const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set([
  'verification_document_name_mismatch',
  'verification_document_address_mismatch',
  'verification_document_dob_mismatch',
  'verification_failed_keyed_identity',
  'verification_document_expired',
  'verification_document_not_readable',
  'verification_document_incomplete',
  'invalid_address_city_state_postal_code',
]);

/**
 * Stripe requirement field names -> connect.json `card.needs.*` keys.
 * Dotted families (individual.address.line1 / .city / .postal_code …)
 * collapse to one entry so the list reads "your home address", not
 * four rows of address parts. Unknown keys render raw — a wrong label
 * would be worse than an ugly one.
 */
function requirementKey(req: string): string | null {
  if (req === 'external_account') return 'bankAccount';
  if (req === 'individual.verification.document') return 'photoId';
  if (req === 'individual.verification.additional_document') return 'secondId';
  if (req === 'individual.verification.proof_of_liveness') return 'selfie';
  if (req === 'individual.id_number') return 'idNumber';
  if (req === 'tos_acceptance.date') return 'tosAcceptance';
  if (req.startsWith('individual.dob')) return 'dob';
  if (req.startsWith('individual.address')) return 'address';
  if (req === 'individual.first_name' || req === 'individual.last_name') return 'legalName';
  return null;
}

/**
 * The lines under the state: guests can't pay yet (every state but
 * ready), the account country (read-only), and the home mismatch. Null
 * when there is nothing to add.
 */
function CardNotes({ status, state, t }: {
  status: ConnectStatus;
  state: ConnectCardState;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const accountCountry = status.has_account ? payoutCountryFrom(status.country ?? null) : null;
  const homeCountry = payoutCountryFrom(status.home_country ?? null);
  // A country outside US and CA still shows, by Stripe's code.
  const label = accountCountry
    ? t(payoutCountryLabelKey(accountCountry))
    : (status.has_account && status.country ? status.country : null);
  const mismatch = accountCountry !== null && homeCountry !== null && countryMismatch(accountCountry, homeCountry);
  const guestsWait = state !== 'ready';
  if (!guestsWait && !label) return null;
  return (
    <div className={styles.notes}>
      {guestsWait && <p className={styles.note}>{t('card.guestsWait')}</p>}
      {label && <p className={styles.note}>{t('country.current', { country: label })}</p>}
      {mismatch && accountCountry && homeCountry && (
        <p className={styles.mismatch} role="status">
          {t('country.mismatch', {
            account: t(payoutCountryTextKey(accountCountry)),
            home: t(payoutCountryTextKey(homeCountry)),
          })}
        </p>
      )}
    </div>
  );
}

export interface ConnectStatusCardProps {
  status: ConnectStatus | undefined;
  loading: boolean;
}

export function ConnectStatusCard({ status, loading }: ConnectStatusCardProps) {
  const { t } = useTranslation('connect');

  if (loading || !status) {
    return (
      <div className={`${styles.card} ${styles.cardMuted}`} aria-busy>
        <span className={styles.icon} aria-hidden><Loader2 size={20} className={styles.spin} /></span>
        <p className={styles.title}>{t('card.checking')}</p>
      </div>
    );
  }

  const state = connectCardState(status);
  const notes = <CardNotes status={status} state={state} t={t} />;

  if (state === 'ready') {
    return (
      <div className={`${styles.card} ${styles.cardReady}`}>
        <span className={styles.icon} aria-hidden><CheckCircle2 size={20} /></span>
        <div className={styles.body}>
          <p className={styles.title}>{t('card.ready')}</p>
          {notes}
        </div>
      </div>
    );
  }

  if (state === 'blocked') {
    const reason = blockedReason(status.requirements_disabled_reason) ?? 'paused';
    return (
      <div className={`${styles.card} ${styles.cardWarn}`} role="alert">
        <span className={styles.icon} aria-hidden><AlertCircle size={20} /></span>
        <div className={styles.body}>
          <p className={styles.title}>{t('card.blockedTitle')}</p>
          <ul className={styles.list}>
            <li className={styles.item}>{t(`card.blocked.${reason}`)}</li>
          </ul>
          {notes}
        </div>
      </div>
    );
  }

  if (state === 'confirming') {
    return (
      <div className={`${styles.card} ${styles.cardMuted}`} aria-busy>
        <span className={styles.icon} aria-hidden><Loader2 size={20} className={styles.spin} /></span>
        <div className={styles.body}>
          <p className={styles.title}>{t('card.confirming')}</p>
          {notes}
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={`${styles.card} ${styles.cardWarn}`} role="alert">
        <span className={styles.icon} aria-hidden><AlertCircle size={20} /></span>
        <div className={styles.body}>
          <p className={styles.title}>{t('card.errorTitle')}</p>
          <ul className={styles.list}>
            {dedupe(errs(status.requirements_errors).map((e) => (
              KNOWN_ERROR_CODES.has(e.code) ? t(`card.errors.${e.code}`) : e.reason
            ))).map((line) => (
              <li key={line} className={styles.item}>{line}</li>
            ))}
          </ul>
          {notes}
        </div>
      </div>
    );
  }

  if (state === 'needed') {
    // Humanised + de-duplicated: four address parts collapse to one
    // "your home address" row.
    const lines = dedupe(arr(status.requirements_currently_due).map((req) => {
      const key = requirementKey(req);
      return key ? t(`card.needs.${key}`) : req;
    }));
    return (
      <div className={`${styles.card} ${styles.cardWarn}`}>
        <span className={styles.icon} aria-hidden><AlertCircle size={20} /></span>
        <div className={styles.body}>
          <p className={styles.title}>{t('card.neededTitle')}</p>
          <ul className={styles.list}>
            {lines.map((line) => (
              <li key={line} className={styles.item}>{line}</li>
            ))}
          </ul>
          {notes}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.card} ${styles.cardMuted}`}>
      <span className={styles.icon} aria-hidden><CircleDashed size={20} /></span>
      <div className={styles.body}>
        <p className={styles.title}>{t('card.notStarted')}</p>
        {notes}
      </div>
    </div>
  );
}

function dedupe(lines: string[]): string[] {
  return Array.from(new Set(lines));
}
